import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Package, Clock, Bookmark, BookmarkCheck, GitCompare, ArrowUpRight } from 'lucide-react';
import { VerificationBadge } from './VerificationBadge';
import { TrustScore } from './TrustScore';
import { useGiwahs } from '../context/GiwahsContext';
import Img from './Img';

const SupplierCard = ({ supplier, index = 0 }) => {
  const { savedSuppliers, compareList, toggleSaved, toggleCompare } = useGiwahs();
  const isSaved = savedSuppliers.includes(supplier.id);
  const isCompared = compareList.includes(supplier.id);

  return (
    <article
      data-testid={`supplier-card-${supplier.id}`}
      className="card-dark group flex flex-col reveal-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <Link to={`/supplier/${supplier.slug}`} className="relative block aspect-[4/3] overflow-hidden bg-[#0A0A0A]">
        <Img
          src={supplier.heroImage}
          alt={`${supplier.name} — ${supplier.type} based in ${supplier.city}, ${supplier.country}`}
          variant="card"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </Link>

      <div className="flex flex-1 flex-col p-5 text-platinum">
        {/* D-080 — verification chips relocated beneath the image */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5" data-testid={`supplier-card-badges-${supplier.id}`}>
            {supplier.badges.map(b => <VerificationBadge key={b} type={b} />)}
          </div>
          <div className="flex gap-1.5 shrink-0">
            <button
              data-testid={`save-supplier-${supplier.id}`}
              onClick={(e) => { e.preventDefault(); toggleSaved(supplier.id); }}
              className={`giwahs-icon-btn ${isSaved ? 'giwahs-icon-active' : ''}`}
              style={{ borderColor: 'rgba(200,169,107,0.55)' }}
              title={isSaved ? 'Saved' : 'Save supplier'}
            >
              {isSaved ? <BookmarkCheck size={13} strokeWidth={1.6} /> : <Bookmark size={13} strokeWidth={1.6} />}
            </button>
            <button
              data-testid={`compare-supplier-${supplier.id}`}
              onClick={(e) => { e.preventDefault(); toggleCompare(supplier.id); }}
              className={`giwahs-icon-btn ${isCompared ? 'giwahs-icon-active' : ''}`}
              style={{ borderColor: 'rgba(200,169,107,0.55)' }}
              title={isCompared ? 'In comparison' : 'Add to compare'}
            >
              <GitCompare size={13} strokeWidth={1.6} />
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="label-overline text-gold">{supplier.type}</span>
          <span className="font-mono-data text-xs text-platinum-2">{supplier.pricingIndicator}</span>
        </div>
        <Link to={`/supplier/${supplier.slug}`} className="mt-3 block">
          <h3 className="font-display text-xl leading-tight tracking-tight md:text-2xl text-platinum" style={{ fontWeight: 600 }}>
            {supplier.name}
          </h3>
        </Link>
        <p className="mt-2 text-sm leading-relaxed text-platinum-2">{supplier.tagline}</p>

        <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-hairline pt-4 font-mono-data text-xs text-platinum-2">
          <div className="flex items-center gap-1.5"><MapPin size={12} strokeWidth={1.5} />{supplier.city}, {supplier.country}</div>
          <div className="flex items-center gap-1.5"><Package size={12} strokeWidth={1.5} />MOQ {supplier.moqMin}+</div>
          <div className="flex items-center gap-1.5"><Clock size={12} strokeWidth={1.5} />{supplier.leadTimeDays}d lead</div>
          <div className="flex items-center gap-1.5">Resp. {supplier.responseRate}%</div>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-5">
          <TrustScore score={supplier.trustScore} variant="inline" />
          <Link
            to={`/supplier/${supplier.slug}`}
            data-testid={`view-supplier-${supplier.id}`}
            className="inline-flex items-center gap-1 border-b pb-0.5 label-overline hover:gap-2 transition-all text-gold"
            style={{ borderColor: '#C8A96B' }}
          >
            View <ArrowUpRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default SupplierCard;
